"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@cmp-src/primitives/Select";
import type {
  ReportCatalog,
  ReportMeasure,
  ReportQuery,
  ReportQueryResult
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useState } from "react";
import { useReportQuery } from "../hooks/useReportQuery";
import { formatDelta, formatValue } from "../lib/formatValue";
import { ResultChart } from "./ResultChart";

interface SplitCompareProps {
  catalog: ReportCatalog;
  query: ReportQuery;
  onClose: () => void;
}

/** The query pinned to one member of a dimension, as a plain trend. */
const pinned = (
  query: ReportQuery,
  dimension: string,
  memberId: string
): ReportQuery => ({
  ...query,
  breakdown: [],
  filters: [
    ...(query.filters ?? []).filter((f) => f.dimension !== dimension),
    { dimension, in: [memberId] }
  ]
});

interface SideProps {
  label: string;
  measure: ReportMeasure | undefined;
  result: ReportQueryResult | undefined;
  other: ReportQueryResult | undefined;
  isLoading: boolean;
}

const Side: React.FC<SideProps> = ({
  label,
  measure,
  result,
  other,
  isLoading
}) => {
  // The delta reads this side against the other, in the measure's terms.
  const delta =
    measure && result && other
      ? formatDelta(measure, result.totals.value, other.totals.value)
      : null;
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-neutral-200 p-4">
      <span className="truncate font-medium text-[13px] text-neutral-700">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "font-semibold text-[28px] text-neutral-800 tabular-nums leading-[32px]",
            isLoading && "opacity-50"
          )}
        >
          {result && measure ? formatValue(measure, result.totals.value) : "—"}
        </span>
        {delta && !delta.isFlat ? (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11.5px] tabular-nums",
              delta.isGood
                ? "bg-[#e8f6e8] text-[#006300]"
                : "bg-[#fbe9e9] text-[#a52a2a]"
            )}
          >
            {delta.text} vs the other
          </span>
        ) : null}
      </div>
      {result ? (
        <ResultChart form="trend" result={result} onDrill={() => undefined} />
      ) : (
        <p className="py-16 text-center text-neutral-400 text-sm">Loading…</p>
      )}
    </div>
  );
};

/**
 * Two members of one dimension, side by side: Lisbon against Porto,
 * Engineering against Sales, this floor against that one. Same question,
 * two filters, one glance.
 */
export const SplitCompare: React.FC<SplitCompareProps> = ({
  catalog,
  query,
  onClose
}) => {
  const measure = catalog.measures.find((m) => m.key === query.measure);
  const pinnedDimensions = new Set(
    (query.filters ?? [])
      .filter((f) => f.in.length === 1)
      .map((f) => f.dimension)
  );
  const dimensions = catalog.dimensions.filter(
    (d) =>
      measure?.dimensions.includes(d.key) &&
      d.members.length >= 2 &&
      !pinnedDimensions.has(d.key)
  );
  const [dimensionKey, setDimensionKey] = useState(dimensions[0]?.key ?? "");
  const dimension = dimensions.find((d) => d.key === dimensionKey);
  const members = dimension?.members ?? [];
  const [a, setA] = useState(members[0]?.id ?? "");
  const [b, setB] = useState(members[1]?.id ?? "");
  const labelOf = (id: string) =>
    members.find((m) => m.id === id)?.label ?? "Pick one";

  const left = useReportQuery(
    dimension && a ? pinned(query, dimension.key, a) : null
  );
  const right = useReportQuery(
    dimension && b ? pinned(query, dimension.key, b) : null
  );

  if (dimensions.length === 0) {
    return (
      <p className="py-16 text-center text-[13px] text-neutral-400">
        Nothing to split this question by.
      </p>
    );
  }

  const memberPicker = (
    value: string,
    onChange: (id: string) => void,
    exclude: string
  ) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[200px] text-[13px]">
        <SelectValue>{labelOf(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-dropdown">
        {members
          .filter((m) => m.id !== exclude)
          .map((m) => (
            <SelectItem key={m.id} value={m.id}>
              {m.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-neutral-500">
        <span>Compare</span>
        <Select
          value={dimensionKey}
          onValueChange={(key) => {
            setDimensionKey(key);
            const next =
              catalog.dimensions.find((d) => d.key === key)?.members ?? [];
            setA(next[0]?.id ?? "");
            setB(next[1]?.id ?? "");
          }}
        >
          <SelectTrigger className="h-8 w-[160px] text-[13px]">
            <SelectValue>{dimension?.label ?? "…"}</SelectValue>
          </SelectTrigger>
          <SelectContent className="z-dropdown">
            {dimensions.map((d) => (
              <SelectItem key={d.key} value={d.key}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {memberPicker(a, setA, b)}
        <span>against</span>
        {memberPicker(b, setB, a)}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="text-neutral-500 hover:text-neutral-900"
        >
          Back to one chart
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Side
          label={labelOf(a)}
          measure={measure}
          result={left.data}
          other={right.data}
          isLoading={left.isLoading}
        />
        <Side
          label={labelOf(b)}
          measure={measure}
          result={right.data}
          other={left.data}
          isLoading={right.isLoading}
        />
      </div>
    </div>
  );
};
